import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiGet, apiPatch, apiPost } from '@services/api';
import useAuthStore from '@store/useAuthStore';

// Fetch current user profile
export function useProfile(options = {}) {
  const setUser = useAuthStore((state) => state.setUser);
  const setPermissions = useAuthStore((state) => state.setPermissions);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  return useQuery({
    queryKey: ['profile'],
    queryFn: async () => {
      const response = await apiGet('/auth/profile');
      // response.data tiene estructura { success: true, data: { user } }
      const userData = response.data?.data;
      if (userData) {
        setUser(userData);
        // También actualizar los permisos si vienen en el response
        if (userData.permissions) {
          setPermissions(userData.permissions);
        }
      }
      return userData;
    },
    enabled: isAuthenticated,
    staleTime: 5 * 60 * 1000, // 5 minutos
    ...options,
  });
}

// Update profile
export function useUpdateProfile() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const currentUser = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiPatch('/profile', data);
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Update auth store with new user data
      if (response?.data) {
        setUser({ ...currentUser, ...response.data });
      }
    },
  });
}

// Update password
export function useUpdatePassword() {
  return useMutation({
    mutationFn: async ({ currentPassword, newPassword }) => {
      const response = await apiPatch('/profile/password', {
        currentPassword,
        newPassword,
      });
      return response.data;
    },
  });
}

// Upload avatar
export function useUploadAvatar() {
  const queryClient = useQueryClient();
  const setUser = useAuthStore((state) => state.setUser);
  const currentUser = useAuthStore((state) => state.user);

  return useMutation({
    mutationFn: async (avatar) => {
      const response = await apiPost('/profile/avatar', { avatar });
      return response.data;
    },
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['profile'] });
      // Update auth store with new avatar
      if (response?.data?.avatar) {
        setUser({ ...currentUser, avatar: response.data.avatar });
      }
    },
  });
}

// Fetch preferences
export function usePreferences(options = {}) {
  return useQuery({
    queryKey: ['preferences'],
    queryFn: async () => {
      const response = await apiGet('/profile/preferences');
      return response.data;
    },
    ...options,
  });
}

// Update preferences
export function useUpdatePreferences() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data) => {
      const response = await apiPatch('/profile/preferences', data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['preferences'] });
    },
  });
}
